/**
 * GrooveLock
 * V11 Feature 3: Adaptive Groove Lock
 * 
 * Detects the user's natural tempo and subtly "magnetizes" 
 * notes toward a detected groove, making everything feel "in the pocket".
 * 
 * - Tempo Detection: Analyzes touch intervals to establish BPM
 * - Magnetic Quantization: Soft pull toward beat positions (not hard quantize)
 * - Syncopation Recognition: Enhances intentional off-beat playing
 * - Ritardando Detection: Identifies slow-downs and stretches bass notes
 */

export interface GrooveContext {
    detectedBPM: number | null;
    confidence: number;           // 0-1 how confident in detected tempo
    beatPhase: number;            // 0-1 position within current beat
    isSyncopatedTouch: boolean;   // Touch is intentionally off-beat
    isRitardando: boolean;        // User is slowing down intentionally
    magneticPull: number;         // -1 to 1, delay adjustment in ms (scaled)
}

export class GrooveLock {
    // Touch timing for BPM detection
    private touchTimestamps: number[] = [];
    private readonly DETECTION_WINDOW = 16; // Need 16 touches for reliable BPM

    // Detected groove
    private detectedBPM: number | null = null;
    private bpmConfidence: number = 0;
    private lastBeatTime: number = 0;

    // Syncopation detection
    private readonly SYNCOPATION_THRESHOLD = 0.15; // Within 15% of beat is on-beat
    private consecutiveOffBeats: number = 0;

    // Ritardando detection
    private intervalTrend: number[] = [];
    private readonly TREND_WINDOW = 6;

    // AUDIT FIX 1.4: Cache last context to avoid re-recording on every call
    private lastContext: GrooveContext | null = null;

    constructor() {
        this.reset();
    }

    reset(): void {
        this.touchTimestamps = [];
        this.detectedBPM = null;
        this.bpmConfidence = 0;
        this.lastBeatTime = 0;
        this.consecutiveOffBeats = 0;
        this.intervalTrend = [];
        this.lastContext = null;
    }

    /**
     * Record a touch and get groove context.
     * Returns timing adjustments and groove state.
     */
    recordTouch(): GrooveContext {
        const now = Date.now();

        // Record timestamp
        if (this.touchTimestamps.length > 0) {
            const interval = now - this.touchTimestamps[this.touchTimestamps.length - 1];
            this.intervalTrend.push(interval);
            if (this.intervalTrend.length > this.TREND_WINDOW) {
                this.intervalTrend.shift();
            }
        }

        this.touchTimestamps.push(now);
        if (this.touchTimestamps.length > this.DETECTION_WINDOW) {
            this.touchTimestamps.shift();
        }

        // Attempt BPM detection
        this.detectTempo();

        // Calculate beat phase
        const beatPhase = this.calculateBeatPhase(now);

        // Detect syncopation
        const isSyncopatedTouch = this.detectSyncopation(beatPhase);

        // Detect ritardando
        const isRitardando = this.detectRitardando();

        // Calculate magnetic pull
        const magneticPull = this.calculateMagneticPull(beatPhase, isSyncopatedTouch);

        // Cache and return context
        this.lastContext = {
            detectedBPM: this.detectedBPM,
            confidence: this.bpmConfidence,
            beatPhase,
            isSyncopatedTouch,
            isRitardando,
            magneticPull
        };

        return this.lastContext;
    }

    /**
     * AUDIT FIX 1.4: Get cached context without recording a new touch.
     * Safe to call multiple times per touch event.
     */
    getContext(): GrooveContext {
        if (this.lastContext !== null) {
            return this.lastContext;
        }
        // Fallback if no touches recorded yet
        return {
            detectedBPM: null,
            confidence: 0,
            beatPhase: 0.5,
            isSyncopatedTouch: false,
            isRitardando: false,
            magneticPull: 0
        };
    }

    /**
     * Detect tempo from touch pattern using median interval analysis.
     */
    private detectTempo(): void {
        if (this.touchTimestamps.length < 4) {
            this.detectedBPM = null;
            this.bpmConfidence = 0;
            return;
        }

        // Calculate intervals
        const intervals: number[] = [];
        for (let i = 1; i < this.touchTimestamps.length; i++) {
            intervals.push(this.touchTimestamps[i] - this.touchTimestamps[i - 1]);
        }

        // Filter out outliers (very fast or very slow touches)
        const filteredIntervals = intervals.filter(i => i > 100 && i < 2000);

        if (filteredIntervals.length < 3) {
            return;
        }

        // Use median for robustness
        const sorted = [...filteredIntervals].sort((a, b) => a - b);
        const median = sorted[Math.floor(sorted.length / 2)];

        // Convert to BPM (assume each touch is roughly a beat or half-beat)
        // Try both beat and half-beat interpretations
        const bpmFromBeat = 60000 / median;
        const bpmFromHalfBeat = 30000 / median;

        // Choose interpretation that gives reasonable BPM (60-180)
        let candidateBPM: number;
        if (bpmFromBeat >= 60 && bpmFromBeat <= 180) {
            candidateBPM = bpmFromBeat;
        } else if (bpmFromHalfBeat >= 60 && bpmFromHalfBeat <= 180) {
            candidateBPM = bpmFromHalfBeat;
        } else if (bpmFromBeat >= 40) {
            candidateBPM = bpmFromBeat;
        } else {
            candidateBPM = bpmFromHalfBeat;
        }

        // Clamp to reasonable range
        candidateBPM = Math.max(50, Math.min(200, candidateBPM));

        // Calculate confidence based on interval consistency
        const variance = this.calculateVariance(filteredIntervals, median);
        const normalizedVariance = variance / (median * median);
        this.bpmConfidence = Math.max(0, Math.min(1, 1 - normalizedVariance * 5));

        // Only update BPM if confidence is reasonable
        if (this.bpmConfidence > 0.3) {
            // Smooth toward new BPM to avoid jumps
            if (this.detectedBPM !== null) {
                this.detectedBPM = this.detectedBPM * 0.7 + candidateBPM * 0.3;
            } else {
                this.detectedBPM = candidateBPM;
            }

            // Update beat reference
            this.lastBeatTime = Date.now();
        }
    }

    /**
     * Calculate variance of intervals.
     */
    private calculateVariance(intervals: number[], mean: number): number {
        if (intervals.length === 0) return 0;
        const squaredDiffs = intervals.map(i => Math.pow(i - mean, 2));
        return squaredDiffs.reduce((a, b) => a + b, 0) / intervals.length;
    }

    /**
     * Calculate current position within the beat cycle (0-1).
     */
    private calculateBeatPhase(now: number): number {
        if (this.detectedBPM === null || this.bpmConfidence < 0.3) {
            return 0.5; // Unknown phase
        }

        const beatDuration = 60000 / this.detectedBPM;
        const timeSinceLastBeat = (now - this.lastBeatTime) % beatDuration;
        return timeSinceLastBeat / beatDuration;
    }

    /**
     * Detect if the touch is an intentional syncopation.
     */
    private detectSyncopation(beatPhase: number): boolean {
        // On-beat = phase near 0 or 1
        // Syncopation = phase near 0.5 (off-beat)
        const distanceFromBeat = Math.min(beatPhase, 1 - beatPhase);
        const isOffBeat = distanceFromBeat > this.SYNCOPATION_THRESHOLD && beatPhase > 0.3 && beatPhase < 0.7;

        if (isOffBeat) {
            this.consecutiveOffBeats++;
        } else {
            this.consecutiveOffBeats = 0;
        }

        // 2+ consecutive off-beats = intentional syncopation
        return this.consecutiveOffBeats >= 2;
    }

    /**
     * Detect if user is slowing down (ritardando).
     */
    private detectRitardando(): boolean {
        if (this.intervalTrend.length < 4) return false;

        // Check if intervals are consistently increasing
        let increasingCount = 0;
        for (let i = 1; i < this.intervalTrend.length; i++) {
            if (this.intervalTrend[i] > this.intervalTrend[i - 1] * 1.1) {
                increasingCount++;
            }
        }

        // 60%+ of intervals increasing = ritardando
        return increasingCount >= this.intervalTrend.length * 0.6;
    }

    /**
     * Calculate magnetic pull toward the beat grid.
     * Returns delay adjustment in milliseconds.
     */
    private calculateMagneticPull(beatPhase: number, isSyncopated: boolean): number {
        if (this.detectedBPM === null || this.bpmConfidence < 0.4) {
            return 0; // No adjustment without confident tempo
        }

        if (isSyncopated) {
            // Don't pull syncopated touches - enhance them instead
            return 0;
        }

        const beatDuration = 60000 / this.detectedBPM;

        // Calculate distance from nearest beat (0, 0.5, or 1)
        let nearestBeatPhase: number;
        if (beatPhase < 0.25) {
            nearestBeatPhase = 0;
        } else if (beatPhase < 0.75) {
            nearestBeatPhase = 0.5;
        } else {
            nearestBeatPhase = 1;
        }

        const phaseError = beatPhase - nearestBeatPhase;

        // Convert phase error to milliseconds
        const errorMs = phaseError * beatDuration;

        // Soft magnetic pull (30% of error, capped at 50ms)
        const pull = -errorMs * 0.3;
        return Math.max(-50, Math.min(50, pull));
    }

    /**
     * Get the detected BPM (null if not confident).
     */
    getDetectedBPM(): number | null {
        return this.bpmConfidence > 0.4 ? this.detectedBPM : null;
    }

    /**
     * Check if groove is locked (confident tempo detected).
     */
    isGrooveLocked(): boolean {
        return this.bpmConfidence > 0.5;
    }
}
